// ── API Client ────────────────────────────────────────────────
// Fetch wrapper with JWT access token management and silent refresh.
// Ported verbatim from the Uniticket frontend (uniform sign-in across apps).

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const API_PREFIX = '/internal/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_PREFIX}${path}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Let the browser set Content-Type for FormData (with multipart boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // send cookies (refresh token)
  });

  // If 401 and we had a token, try silent refresh then retry once
  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText);
  }

  return res.json() as Promise<T>;
}

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

/**
 * Like {@link apiFetch} but returns a binary Blob (+ the server-supplied filename)
 * instead of JSON — for file downloads (e.g. the export .xlsx). Carries the Bearer
 * token (a plain <a href> would not) and does the same one-shot silent refresh.
 */
export async function apiDownload(path: string, options: RequestInit = {}): Promise<DownloadResult> {
  const url = `${API_PREFIX}${path}`;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, { ...options, headers, credentials: 'include' });

  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers, credentials: 'include' });
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message || res.statusText);
  }

  const blob = await res.blob();
  return { blob, filename: filenameFromDisposition(res.headers.get('Content-Disposition')) };
}

/** Extract `filename="…"` from a Content-Disposition header (fallback otherwise).
 *  Handles the quoted form and the RFC 5987 `filename*=UTF-8'<lang>'…` form. */
function filenameFromDisposition(header: string | null): string {
  const match = header ? /filename\*?=(?:UTF-8'[^']*')?"?([^";]+)"?/i.exec(header) : null;
  if (!match) return 'export.xlsx';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed percent-escape would throw — keep the raw (already-downloaded) name.
    return match[1];
  }
}

async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      accessToken = null;
      return false;
    }

    const data = await res.json();
    accessToken = data.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}
